import { createContext } from "react";
import type { InputResponse } from "eve/client";
import type { QuestionBlock } from "./projection";

export interface AskContextValue {
  /** The session is parked and idle — answers can go out now. */
  canAnswer: boolean;
  readOnly: boolean;
  /** Answers being delivered, by requestId. */
  answering: Readonly<Record<string, InputResponse>>;
  /** The question number keys (1–9) pick options for. */
  keyboardTarget?: string;
  onAnswer: (requestId: string, response: { optionId?: string; text?: string }) => void;
  /** Several answers in one send — a questionnaire's batch. */
  onAnswerMany: (responses: InputResponse[]) => void;
}

/** Answering state lives in context so a click or a delivery never
 * re-renders (and re-parses) the memoized transcript rows. */
export const AskContext = createContext<AskContextValue>({
  canAnswer: false,
  readOnly: true,
  answering: {},
  onAnswer: () => {},
  onAnswerMany: () => {},
});

export const isApproval = (q: QuestionBlock): boolean =>
  q.request.display === "confirmation" ||
  (q.request.action.toolName !== "ask_question" &&
    (q.request.options ?? []).some((o) => o.id === "approve"));

/** What the user answered, as a short label (undefined when unknown). */
export function answerLabel(q: QuestionBlock): string | undefined {
  const r = q.response;
  if (!r) return undefined;
  const text =
    (r.optionId && q.request.options?.find((o) => o.id === r.optionId)?.label) || r.text || r.optionId;
  if (!text) return undefined;
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}
